import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import GobActivity, { GobHourActivity, TopToolChange } from "../../models/activity/gobActivity";
import DefectActivity from "../../models/activity/defectActivity";
import { MachineActivity, SectionActivity, ShopActivity } from "../../models/activity/shopActivity";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import UIUtilityServiceName, { EnumOption, UIUtilityService } from "../../services/utility/uiUtility.service";
import { DefectType } from "../../models/defectType";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import ToolChangeServiceName, { ToolChangeService } from "../../services/toolChange/toolChange.service";
import DefectTypeServiceName, { DefectTypeService } from "../../services/defects/defectType.service";
import NotifyServiceName, { NotifyService } from "../../services/notify/notify.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import NewGuidServiceName, { NewGuidService } from "../../services/utility/newguid.service";
import AuthServiceName, { AuthService } from "../../services/auth/auth.service";
import { apsCommands } from "../../shared/dto/aps.commands";
import { apsEvents } from "../../shared/dto/aps.events";
import template from './addToolChange.tpl.html';

let requires = [UIUtilityServiceName, LoaderServiceName, ToolChangeServiceName, DefectTypeServiceName,
  NotifyServiceName, NewGuidServiceName, AuthServiceName];

interface GobSelection {
  gob: GobActivity;
  currentMold(): any;
  toolChanges: any[];
  selected: boolean;
  defects(): DefectActivity[];
}

interface SectionSelection {
  section: SectionActivity;
  gobs: GobSelection[];
  toolChanges: any[];
  selected: boolean;
  defects(): DefectActivity[];
}

interface ToolAndType extends apsViewModels.ToolAndType {
  selected: boolean;
}

interface ToolChange {
  id: string;
  reason: apsEnums.ToolChangeReason;
  tool: ToolAndType;
  moldNumber: number;
  section: SectionActivity;
  gob: GobActivity;
  unlistedDefect: boolean;
  defectFixed: apsViewModels.DefectActivity;
  defectTypeId: string;
  comment: string;
  alphaNumber: string;
  otherReason: string;
  moldInUse(): boolean;
  alreadyChanged(): boolean;
  defects(): DefectActivity[];
  reasonChanged(): void;
  isFixDefect(): boolean;
  exclude: boolean;
  max: number;
}

export interface LoadToolChangeData {
  shopId: string;
  activity: ShopActivity;
  gob: GobActivity;
  gobHour: GobHourActivity;
  date: Date;
  defectFixed?: DefectActivity;
  sectionChangeId?: string;
  sectionChangeSectionId?: string;
  toolChanges?: TopToolChange;
  defectCause?: apsViewModels.DefectCauseSelection;
}

export function openAddToolChange(
  data: LoadToolChangeData,
  $modal: mm.foundation.modal): angular.IPromise<apsEvents.ToolChangeBase> {
  return $modal.open({
    template: template,
    controller: 'AddToolChangeCtrl as vm',
    resolve: {
      data: function () {
        return data;
      }
    }
  }).result;
}

interface ToolChangeController {
  shopId: string;
  activity: ShopActivity;
  gob: GobActivity;
  section: SectionActivity;
  machine: MachineActivity;
  sections: SectionActivity[];
  minTime: string;
  maxTime: string;
  timeChanged: Date;
  requalificationDateTime: Date;
  requalification: boolean;
  requalificationMinTime(): string;
  gobs: GobActivity[];
  selections: SectionSelection[];
  machineToolChanges: any[];
  defectFixed: DefectActivity;
  reasons: EnumOption[];
  reason: apsEnums.ToolChangeReason;
  useDefaultReason: boolean;
  otherReason: string;
  defectCause: apsViewModels.DefectCauseSelection;
  otherReasonRequired(): boolean;
  otherReasonChanged(): void;
  timeZone: string;
  isFixDefect(): boolean;
  useDefaultReasonChanged(): void;
  toolChanges(): ToolChange[];
  defects(): DefectActivity[];
  tools: ToolAndType[];
  cancel(): void;
  addToolChange(): void;
  form: angular.IFormController;
  duration: number;
  machineTabActive: boolean;
  comment: string;
  isEdit: boolean;
  readyForSubmit: boolean;
  loadingSectionChange: boolean;
  defectTypes: DefectType[];
}

export default angular.module('aps.controller.toolChange', requires)
  .controller('AddToolChangeCtrl', addToolChangeCtrl)
  .name;

function addToolChangeCtrl(
  $modalInstance: mm.foundation.modalInstance,
  loaderService: LoaderService,
  toolChangeService: ToolChangeService,
  defectTypeService: DefectTypeService,
  data: LoadToolChangeData,
  notifyService: NotifyService,
  uiUtilityService: UIUtilityService,
  $q: angular.IQService,
  newGuidService: NewGuidService,
  authService: AuthService
) {
  var vm = this as ToolChangeController;

  vm.shopId = data.shopId;
  vm.activity = data.activity;
  vm.gob = data.gob;
  vm.section = vm.gob.section;
  vm.machine = vm.section.machine;
  vm.sections = vm.machine.sections;
  vm.timeZone = vm.activity.timeZoneId;
  vm.machineTabActive = data.gob.isMachineLevel;
  vm.readyForSubmit = false;
  vm.loadingSectionChange = false;
  vm.defectCause = data.defectCause;

  var range = DateUtilityService.getHourRange(data.date, vm.activity.timeZoneId);
  vm.minTime = range.minTime;
  vm.maxTime = range.maxTime;
  if (data.sectionChangeId) {
    var hourIndex = 0;
    while (hourIndex < vm.activity.hours.length && !vm.activity.hours[hourIndex].isLocked) {
      hourIndex++;
    }
    var startTime = moment(vm.activity.hours[hourIndex].startTime);
    vm.minTime = DateUtilityService.formatISO(startTime, vm.activity.timeZoneId);
  }

  if (data.toolChanges) {
    vm.timeChanged = data.toolChanges.timeChanged;
  } else {
    vm.timeChanged = range.initialTime.toDate();
  }

  var id, commentId, userData: apsViewModels.UserData;
  var promises = [];
  promises.push(authService.getUserData().then(x => userData = x));

  if (data.toolChanges) {
    vm.isEdit = true;
  } else {
    promises.push(newGuidService.getNewGuid('toolChange').then(x => id = x));
    promises.push(newGuidService.getNewGuid('toolChangeComment').then(x => commentId = x));
  }

  vm.requalificationDateTime = vm.timeChanged;
  vm.requalificationMinTime = () => {
    if (vm.timeChanged) {
      return DateUtilityService.formatISO(vm.timeChanged, vm.timeZone);
    }
    return vm.minTime;
  };

  vm.requalification = false;

  var getCurrentMold = (gob: GobActivity) => {
    return (): any => {
      var mold = gob.moldOnDate(vm.timeChanged);
      if (mold) {
        return mold.toolNumber;
      }
      return gob.section.name + gob.name;
    };
  };

  vm.gobs = vm.sections[0].gobs;
  var selections: SectionSelection[] = [];
  vm.defects = () => {
    var defects = <DefectActivity[]>[];
    _.forEach(vm.selections, (sectionSelection: SectionSelection) => {
      defects = defects.concat(sectionSelection.defects());
    });
    _.forEach(vm.machine.machineLevelActivity.defects, (defect: DefectActivity) => {
      if (!_.find(defects, { id: defect.id })) {
        defect.nameAndLocation = defect.name;
        defects.push(defect);
      }
    });
    return defects;
  };
  _.forEach(vm.sections, (section: SectionActivity) => {
    var sectionSelection: SectionSelection = {
      section: section,
      gobs: [],
      toolChanges: [],
      selected: !vm.isEdit && vm.section === section,
      defects: () => {
        var defects = <DefectActivity[]>[];
        _.forEach(sectionSelection.gobs, (gobSelection: GobSelection) => {
          defects = defects.concat(gobSelection.defects());
        });
        return defects;
      }
    };
    _.forEach(section.gobs, (gob: GobActivity) => {
      sectionSelection.gobs.push(<GobSelection>{
        gob: gob,
        currentMold: getCurrentMold(gob),
        toolChanges: [],
        selected: !vm.isEdit && vm.gob === gob,
        defects: () => {
          var defects = <DefectActivity[]>[];
          _.forEach(gob.defects, (defect: DefectActivity) => {
            if (!_.find(defects, { id: defect.id })) {
              defect.nameAndLocation = defect.name + ' (' +
                gob.section.name + gob.name + ')';
              defects.push(defect);
            }
          });
          return defects;
        }
      });
    });
    selections.push(sectionSelection);
  });
  vm.selections = selections;
  vm.machineToolChanges = [];

  vm.defectFixed = data.defectFixed;
  vm.reasons = uiUtilityService.getListOptionsFromEnum(apsEnums.ToolChangeReason, 0);
  vm.reason = vm.defectFixed ? apsEnums.ToolChangeReason.FixDefect : undefined;
  vm.useDefaultReason = true;
  vm.otherReason = null;

  vm.isFixDefect = () => (vm.reason === apsEnums.ToolChangeReason.FixDefect);

  vm.useDefaultReasonChanged = function () {
    var fixDefect = vm.isFixDefect();

    if (vm.useDefaultReason !== true) {
      return;
    }
    _.forEach(vm.toolChanges(), (toolChange: ToolChange) => {
      toolChange.reason = vm.reason;
      if (fixDefect && vm.defectFixed) {
        toolChange.defectFixed = _.find(toolChange.defects(), { id: vm.defectFixed.id });
      } else if (fixDefect && !vm.defectFixed) {
        var defects = toolChange.defects();
        if (defects && defects.length > 0) {
          toolChange.defectFixed = defects[0];
        }
      } else {
        toolChange.defectFixed = undefined;
      }
    });
  };

  var getAlreadyChanged = (toolChange: ToolChange) => {
    var index = data.activity.getHourIndex(vm.timeChanged);
    var gobHour: GobHourActivity;
    if (toolChange.tool.type === apsEnums.ToolType.Machine) {
      gobHour = vm.machine.machineLevelActivity.hours[index];
    } else if (toolChange.tool.type === apsEnums.ToolType.Variable) {
      gobHour = toolChange.section.gobs[0].hours[index];
    } else {
      gobHour = toolChange.gob.hours[index];
    }
    return _.some(gobHour.activity, (a: TopToolChange) =>
      a.isToolChange
      && a.timeChanged.getTime() === vm.timeChanged.getTime()
      && _.some(a.tools, t => t.toolId === toolChange.tool.id && t.id !== toolChange.id));
  };

  var getMoldInUse = function (toolChange: ToolChange) {
    if (!toolChange.moldNumber) {
      return false;
    }

    var inUse = false;

    // changes
    var toolChanges = vm.toolChanges();
    var moldChanges = _.filter(toolChanges, t => t.tool.isMold);
    _.forEach(moldChanges, (mc) => {
      if (mc !== toolChange && mc.moldNumber === toolChange.moldNumber) {
        inUse = true;
      }
    });

    if (inUse
      || toolChange.reason === apsEnums.ToolChangeReason.JobChange
      || toolChange.reason === apsEnums.ToolChangeReason.MoldSwap) {
      return inUse;
    }

    // all mold numbers in use in the shop - skip for job change and mold swap
    _.forEach(vm.activity.gobs, function (gob: GobActivity) {
      var mold = gob.moldOnDate(vm.timeChanged, toolChange.id);
      if (mold.toolNumber === toolChange.moldNumber) {
        inUse = true;
      }
    });
    return inUse;
  };

  var getToolChanges = function (
    toolChanges: ToolChange[],
    typeName,
    sectionSelection?: SectionSelection,
    gobSelection?: GobSelection) {
    var tools = _.filter(vm.tools, { typeName: typeName, selected: true });
    var matchingToolChanges = [];
    _.forEach(tools, function (tool: ToolAndType) {
      var toolChange: ToolChange = _.find(toolChanges, { tool: tool });
      if (!toolChange) {
        toolChange = {
          id: undefined,
          section: sectionSelection ? sectionSelection.section : undefined,
          gob: gobSelection ? gobSelection.gob : undefined,
          tool: tool,
          exclude: false,
          moldNumber: undefined,
          reason: vm.useDefaultReason ? vm.reason : undefined,
          unlistedDefect: false,
          defectFixed: undefined,
          defectTypeId: undefined,
          comment: undefined,
          alphaNumber: undefined,
          otherReason: vm.otherReason,
          moldInUse: () => false,
          alreadyChanged: () => false,
          defects: () => {
            if (sectionSelection) {
              if (gobSelection) {
                return gobSelection.defects();
              }
              return sectionSelection.defects();
            }
            return vm.defects();
          },
          reasonChanged: function () {
            if (toolChange.isFixDefect()) {
              toolChange.defectFixed = undefined;
            }
          },
          isFixDefect: () => toolChange.reason === apsEnums.ToolChangeReason.FixDefect,
          max: tool.isMold ? 127 : 999
        };

        if (!vm.loadingSectionChange) {
          vm.readyForSubmit = false;
          newGuidService.getNewGuid('toolChangeDetail').then(id => {
            toolChange.id = id;
            vm.readyForSubmit = true;
          });
        }

        if (vm.defectFixed && toolChange.isFixDefect()) {
          toolChange.defectFixed = _.find(toolChange.defects(), { id: vm.defectFixed.id });
        }
        if (!vm.defectFixed && toolChange.isFixDefect()) {
          var defects = toolChange.defects();
          if (defects && defects.length > 0) {
            toolChange.defectFixed = defects[0];
          }
        }
        if (tool.isMold) {
          toolChange.moldInUse = () => getMoldInUse(toolChange);
        }
        toolChange.alreadyChanged = () => getAlreadyChanged(toolChange);
        toolChanges.push(toolChange);
      }
      matchingToolChanges.push(toolChange);
    });
    return matchingToolChanges;
  };

  vm.toolChanges = function () {
    var toolChanges = [];
    _.forEach(vm.selections, (selection: SectionSelection) => {
      _.forEach(selection.gobs, (gobSelection: GobSelection) => {
        if (!gobSelection.selected) {
          return;
        }

        _.forEach(getToolChanges(gobSelection.toolChanges, 'Mold', selection, gobSelection),
          function (toolChange) {
            toolChanges.push(toolChange);
          });
      });

      if (!selection.selected) {
        return;
      }

      _.forEach(getToolChanges(selection.toolChanges, 'Variable', selection),
        function (toolChange) {
          toolChanges.push(toolChange);
        });
    });

    _.forEach(getToolChanges(vm.machineToolChanges, 'Machine'),
      function (toolChange) {
        toolChanges.push(toolChange);
      });
    return toolChanges;
  };

  var toolPromise = toolChangeService.getToolsAndTypes()
    .then((toolsAndTypes: ToolAndType[]) => {
      _.forEach(toolsAndTypes, (tool: ToolAndType) => {
        tool.selected = false;
      });
      vm.tools = toolsAndTypes;
    });
  promises.push(toolPromise);

  var sectionChange: apsViewModels.SectionChangeDetails;
  if (data.sectionChangeId) {
    var sectionChangePromise = toolChangeService
      .getSectionChange(data.sectionChangeId, data.sectionChangeSectionId, data.shopId)
      .then((data) => {
        sectionChange = data;
      });
    promises.push(sectionChangePromise);
  }

  var editToolChange: apsViewModels.TopToolChangeDetail;
  if (data.toolChanges) {
    promises.push(toolChangeService
      .getToolChange(data.toolChanges.id)
      .then((data) => {
        editToolChange = data;
      }));
  }

  var populateToolChange = function () {
    var reason = undefined;
    var multipleReasons = false;
    _.forEach(editToolChange.toolChanges, (tc: apsViewModels.ToolChangeItem) => {
      var toolMatch = _.find(vm.tools, { id: tc.toolId });
      toolMatch.selected = true;

      if (toolMatch.type === apsEnums.ToolType.Mold || toolMatch.type === apsEnums.ToolType.Variable) {
        var sectionSelection = _.find(vm.selections, s => s.section.sectionNumber === tc.section);
        if (sectionSelection) {
          if (toolMatch.type === apsEnums.ToolType.Mold) {
            var gobSelection = _.find(sectionSelection.gobs, g => g.gob.gobNumber === tc.gob);
            if (gobSelection) {
              gobSelection.selected = true;
            }
          } else {
            sectionSelection.selected = true;
          }
        }
      }
    });

    _.forEach(vm.toolChanges(), (tc: ToolChange) => {
      var toolChange = _.find(editToolChange.toolChanges, {
        section: tc.section ? tc.section.sectionNumber : null,
        gob: tc.gob ? tc.gob.gobNumber : null,
        toolId: tc.tool.id
      });
      if (toolChange) {
        tc.id = toolChange.id;
        tc.comment = toolChange.comment;
        tc.alphaNumber = toolChange.alphaNumber;
        tc.reason = toolChange.reason;
        if (!reason) {
          reason = tc.reason;
        } else if (reason !== tc.reason) {
          multipleReasons = true;
        }
        if (toolChange.defectId) {
          tc.defectFixed = _.find(tc.defects(), { id: toolChange.defectId });
        } else if (toolChange.unlistedDefect && toolChange.defectTypeId) {
          tc.unlistedDefect = true;
          tc.defectTypeId = toolChange.defectTypeId;
        }
        tc.otherReason = toolChange.otherReason;
        tc.moldNumber = toolChange.toolNumber;
      } else {
        tc.exclude = true;
      }
    });

    if (multipleReasons) {
      vm.useDefaultReason = false;
    } else {
      vm.useDefaultReason = true;
      vm.reason = reason;
    }
    vm.duration = editToolChange.duration;
    vm.timeChanged = editToolChange.timeChanged;
  };

  var jobRun = _.find(vm.activity.jobRuns,
    (jr) => jr.startDate <= vm.timeChanged && (!jr.endDate || vm.timeChanged < jr.endDate));
  var defectTypesPromise = defectTypeService.getDefectTypesAndClassifications()
    .then((data) => {
      if (jobRun && jobRun.excludedDefectTypeIds && jobRun.excludedDefectTypeIds.length > 0) {
        data.types = _.filter(data.types, dt => _.every(jobRun.excludedDefectTypeIds, xt => xt !== dt.id));
      }
      _.forEach(data.types, (type: DefectType) => {
        if (jobRun && type.wareClasses) {
          var override = _.find(type.wareClasses, (w) => w.wareClassId === jobRun.wareClassId);
          if (override && override.severity !== null) {
            type.severity = override.severity;
          }
        }
      });
      vm.defectTypes = data.types;
    });

  promises.push(defectTypesPromise);

  loaderService.loadingPromise($q.all(promises).then(() => {
    if (editToolChange) {
      vm.loadingSectionChange = true;
      populateToolChange();
      vm.loadingSectionChange = false;
      vm.readyForSubmit = true;
      return;
    }

    if (vm.defectCause) {
      if (vm.defectCause.toolId) {
        var tool = _.find(vm.tools, { id: vm.defectCause.toolId });
        if (tool) {
          tool.selected = true;
        }
      }
      if (vm.defectCause.reason) {
        vm.reason = vm.defectCause.reason;
      }
    }

    if (!sectionChange) {
      vm.readyForSubmit = true;
      return;
    }

    vm.reason = apsEnums.ToolChangeReason.SectionChange;
    _.forEach(sectionChange.tools, (tool: ToolAndType) => {
      var toolMatch = _.find(vm.tools, { id: tool.id });
      toolMatch.selected = true;
    });

    var sectionSelection = _.find(vm.selections, s => s.section.sectionNumber === sectionChange.section);
    sectionSelection.selected = true;
    _.forEach(sectionSelection.gobs, gob => gob.selected = true);

    vm.loadingSectionChange = true;
    var sectionChangePromises = [];
    _.forEach(_.filter(vm.toolChanges(), (t: ToolChange) => !t.id), (t: ToolChange) => {
      sectionChangePromises.push(newGuidService.getNewGuid('toolChangeDetail').then(id => t.id = id));
    });
    if (sectionChangePromises.length > 0) {
      $q.all(sectionChangePromises).then(() => vm.readyForSubmit = true);
    }
    vm.loadingSectionChange = false;
  }));

  vm.cancel = function () {
    $modalInstance.close();
  };

  vm.addToolChange = function () {
    if (!vm.readyForSubmit || vm.form.$invalid) {
      return;
    }

    var toolChanges = vm.toolChanges();
    if (toolChanges.length === 0) {
      return;
    }

    var invalid = false;
    var commandToolChanges: apsCommands.AddToolChangeDetail[] = [];
    _.forEach(toolChanges, function (tc: ToolChange) {
      if (tc.exclude) {
        return;
      }

      if (tc.moldInUse() || tc.alreadyChanged()) {
        invalid = true;
        return false;
      }

      if (tc.isFixDefect()) {
        if (tc.unlistedDefect ? !tc.defectTypeId : !tc.defectFixed) {
          invalid = true;
          return false;
        }
      }

      commandToolChanges.push({
        id: tc.id,
        section: tc.section ? tc.section.sectionNumber : undefined,
        gob: tc.gob ? tc.gob.gobNumber : undefined,
        unlistedDefect: tc.isFixDefect() ? tc.unlistedDefect : undefined,
        defectId: tc.isFixDefect() && !tc.unlistedDefect ? tc.defectFixed.id : undefined,
        defectTypeId: tc.isFixDefect() && tc.unlistedDefect ? tc.defectTypeId : undefined,
        reason: tc.reason,
        toolId: tc.tool.id,
        toolNumber: tc.moldNumber,
        comment: tc.comment,
        alphaNumber: tc.alphaNumber,
        otherReason: tc.otherReason
      });
    });

    if (invalid) {
      return;
    }

    var toolAddedEvents: apsEvents.ToolChangeDetailAdded[] = [];
    _.forEach(_.filter(toolChanges, tc => !tc.exclude), (tc) => {
      toolAddedEvents.push({
        id: tc.id,
        section: tc.section ? tc.section.sectionNumber : undefined,
        gob: tc.gob ? tc.gob.gobNumber : undefined,
        gobName: tc.gob ? tc.gob.name : undefined,
        unlistedDefect: tc.isFixDefect() ? tc.unlistedDefect : undefined,
        defectId: tc.isFixDefect() && !tc.unlistedDefect
          ? tc.defectFixed.id
          : undefined,
        defectTypeId: tc.isFixDefect()
          ? (tc.unlistedDefect
            ? tc.defectTypeId
            : tc.defectFixed.defectTypeId)
          : undefined,
        defectTypeName: tc.isFixDefect()
          ? (tc.unlistedDefect
            ? (_.find(vm.defectTypes, (dt) => dt.id === tc.defectTypeId).name)
            : tc.defectFixed.name)
          : undefined,
        reason: tc.reason,
        otherReason: tc.otherReason,
        toolId: tc.tool.id,
        toolNumber: tc.moldNumber,
        toolName: tc.tool.name,
        toolIsMold: tc.tool.isMold,
        comment: tc.comment,
        legacyId: null
      });
    });

    if (editToolChange) {
      var editCommand: apsCommands.EditToolChange = {
        id: editToolChange.id,
        shopId: vm.shopId,
        isMachineId: vm.machine.id,
        timeChanged: vm.timeChanged,
        duration: vm.duration,
        toolChanges: commandToolChanges
      };

      var comments: apsEvents.ToolChangeCommentAdded[] = [];
      _.forEach(editToolChange.comments, (c: apsViewModels.ToolChangeDetailsComment) => {
        comments.push({
          shopId: null,
          plantId: undefined,
          isMachineId: null,
          section: null,
          gob: null,
          gobName: null,
          toolChangeId: editToolChange.id,
          comment: c.comment,
          createdByFullName: c.createdByFullName,
          timeChanged: vm.timeChanged,
          id: c.id,
          userId: null,
          eventDate: c.createdOn
        });
      });

      var editEvent: apsEvents.ToolChangeEdited = {
        id: editCommand.id,
        plantId: undefined,
        shopId: editCommand.shopId,
        isMachineId: editCommand.isMachineId,
        jobRunId: undefined, // not used
        duration: editCommand.duration,
        toolChanges: toolAddedEvents,
        timeChanged: editCommand.timeChanged,
        createdByFullName: userData.fullName,
        rootComments: comments,
        userId: userData.id,
        eventDate: editCommand.timeChanged,
        deletedToolChanges: []
      };

      loaderService.loadingPromise(toolChangeService.editToolChange(editCommand)
        .then(function () {
          $modalInstance.close(editEvent);
        }, notifyService.handleApiErrors));

    } else {

      var addCommand: apsCommands.AddToolChange = {
        id: id,
        shopId: vm.shopId,
        isMachineId: vm.machine.id,
        timeChanged: vm.timeChanged,
        duration: vm.duration,
        comment: vm.comment,
        commentId: vm.comment ? commentId : undefined,
        toolChanges: commandToolChanges,
        requalification: vm.requalification,
        requalificationDateTime: vm.requalification
          ? vm.requalificationDateTime
          : undefined
      };

      var addEvent: apsEvents.ToolChangeAdded = {
        id: addCommand.id,
        plantId: undefined,
        shopId: addCommand.shopId,
        isMachineId: addCommand.isMachineId,
        jobRunId: undefined, // not used
        duration: addCommand.duration,
        toolChanges: toolAddedEvents,
        timeChanged: addCommand.timeChanged,
        createdByFullName: userData.fullName,
        rootComments: [],
        userId: userData.id,
        eventDate: addCommand.timeChanged
      };

      if (addCommand.comment) {
        addEvent.rootComments.push({
          id: addCommand.commentId,
          plantId: undefined,
          shopId: addCommand.shopId,
          isMachineId: addCommand.isMachineId,
          section: undefined,
          gob: undefined,
          gobName: undefined,
          toolChangeId: addCommand.id,
          comment: addCommand.comment,
          createdByFullName: userData.fullName,
          timeChanged: addCommand.timeChanged,
          userId: userData.id,
          eventDate: addCommand.timeChanged
        });
      }

      loaderService.loadingPromise(toolChangeService.addToolChange(addCommand)
        .then(function () {
          $modalInstance.close(addEvent);
        }, notifyService.handleApiErrors));
    }
  };

  vm.otherReasonRequired = function () {
    return apsEnums.ToolChangeReason.Other === vm.reason;
  };

  vm.otherReasonChanged = function () {
    var otherReasonToSet = vm.otherReasonRequired() ? vm.otherReason : null;

    _.forEach(vm.toolChanges(), (toolChange: ToolChange) => {
      toolChange.otherReason = otherReasonToSet;
    });
  };
}


// WEBPACK FOOTER //
// ./src/app/controllers/toolChange/addToolChange.controller.ts